import type { CoverLayout } from '../types/reportTemplate';
import {
  REPORT_SIDEBAR_CLEARANCE_PX_CSS,
  REPORT_SIDE_GUTTER_PX_CSS,
} from './reportSpacingTokens';

/** Cover variants that omit the cyan confidential left rail on all pages. */
const NO_CONFIDENTIAL_SIDEBAR_LAYOUTS: ReadonlySet<CoverLayout> = new Set([
  'hero-image',
  'minimal',
  'data-dense',
]);

/** True when the classic left "Strictly Confidential" sidebar should render. */
export function shouldShowConfidentialSidebar(
  coverLayout: CoverLayout | undefined,
): boolean {
  if (!coverLayout) return true;
  return !NO_CONFIDENTIAL_SIDEBAR_LAYOUTS.has(coverLayout);
}

/** @deprecated Prefer `shouldShowConfidentialSidebar` — kept for call-site clarity. */
export function isMinimalOrDataDenseTemplate(coverLayout: CoverLayout | undefined): boolean {
  return coverLayout === 'minimal' || coverLayout === 'data-dense';
}

/**
 * Tailwind classes for the absolutely positioned confidential rail.
 * `w-10` = 40px = `REPORT_SIDEBAR_RAIL_PX` (reportSpacingTokens); the in-flow
 * content clearance (`reportContentPaddingClass`) derives the matching 70px
 * inset from that token + the 30px breathing margin.
 */
export const CONFIDENTIAL_SIDEBAR_CLASS =
  'absolute left-0 top-0 bottom-0 w-10 flex items-center justify-center overflow-hidden';

// K2 — the side gutter (`36px`) + the rail clearance (`70px` = 40 rail + 30
// margin) are derived from reportSpacingTokens so the geometry lives in ONE
// place. The arbitrary-value Tailwind utilities below interpolate the token
// strings, producing the SAME classes (`lg:p-[36px]`, `lg:pl-[70px]`) the
// chrome test asserts — no rendered change, just a killed magic number.
const GUTTER = REPORT_SIDE_GUTTER_PX_CSS; // '36px'
const RAIL_CLEAR = REPORT_SIDEBAR_CLEARANCE_PX_CSS; // '70px'

/**
 * Page/section shell padding — omits left inset when the rail is shown so
 * `left-0` on the sidebar aligns with the page edge.
 */
export function reportShellPaddingClass(omitSectionSidebar: boolean): string {
  return omitSectionSidebar
    ? `p-4 sm:p-8 lg:p-[${GUTTER}]`
    : `p-4 sm:p-8 lg:p-[${GUTTER}] lg:pl-0`;
}

/** In-flow content inset clearing the 40px rail + 30px margin (70px total). */
export function reportContentPaddingClass(omitSectionSidebar: boolean): string {
  return omitSectionSidebar ? '' : `pl-0 sm:pl-[${RAIL_CLEAR}]`;
}

/**
 * Combined horizontal inset — prefer `reportShellPaddingClass` +
 * `reportContentPaddingClass` when rendering a sidebar on the same shell.
 */
export function reportSectionPaddingClass(omitSectionSidebar: boolean): string {
  // Responsive horizontal inset: a flat large gutter wasted ~32% of a 375px
  // phone and forced report side-scroll. Scale 16→32→36px (71786e33 — tighter
  // standard margins) and only reserve the 70px rail clearance from `lg` up
  // (the confidential rail is lg-only too).
  return omitSectionSidebar
    ? `px-4 sm:px-8 lg:px-[${GUTTER}]`
    : `px-4 sm:px-8 lg:px-[${GUTTER}] lg:pl-[${RAIL_CLEAR}]`;
}

/** CSS class applied when a section renders in a 2-column page layout. */
export const REPORT_SECTION_PAGE_COLS_2_CLASS = 'report-section-page-cols-2';
